/**
 * DayPicker Component
 */

import React, { FC, useState, useEffect } from 'react';
import { withTheme } from 'styled-components';

import { DayPickerProps } from './dayPicker.types';
import {
  DayPickerWrapper,
  Slide,
  SlideWrapper,
  DateSlide,
  DisabledDateSlide,
  InnerSlideWrapper,
  InnerDateWrapper,
  InnerSlideTextWrapper
} from './dayPicker.style';

import { useSwipeable } from 'react-swipeable';

import { motion, useAnimation } from 'framer-motion';

import add from 'date-fns/add';
import format from 'date-fns/format';
import sub from 'date-fns/sub';
import isBefore from 'date-fns/isBefore';
import isAfter from 'date-fns/isAfter';
import isSameDay from 'date-fns/isSameDay';
import es from 'date-fns/locale/es';
import { AnyStyledComponent } from 'styled-components';
import Skeleton from '../Skeleton/skeleton';
import Button from '../Button/button';
import { ButtonShapes, ButtonSizes, ButtonStyles } from '../Button/button.types';
import { useMedia } from '../../utils/hooks/useMedia/useMedia';
import BREAKPOINTS from '../../style/breakpoints';
import makeSafeDate from '../../utils/makeSafeDate';

import Handler from '../../commonTypes/handler.types';

const standFormat: string = 'M-dd-yyyy';
const propFormat: string = 'yyyy-MM-dd';

const LEFT: string = 'left';
const RIGHT: string = 'right';
const RESET: string = 'reset';

const longMonthFormat: string = 'MMMM';
const shortMonthFormat: string = 'MMM';
const yearFormat: string = 'yyyy';

const SPANISH_DE: string = 'de';

// Build an entire date array
const buildDateArr = (size: number, selectedDate: Date, centerIndex: number, datesPerSlide: number) => {
  let dateArr = [];

  for (let i: number = 0; i < size; i++) {
    const dateOffset = Math.abs(centerIndex - i) * datesPerSlide;
    const operation = i < centerIndex ? sub : add;
    dateArr.push(operation(selectedDate, {days: dateOffset}));
  }
  return dateArr;
}

// Build a supplemental amount of dates to be appended to current array
const buildExtraDates = (direction: string, num: number, extreme: Date, datesPerSlide: number) => {
  const operation = direction === LEFT ? add : sub;

  let extraDates = [];
  for (let i = num - 1; i > -1; i--) {
      extraDates.push(operation(extreme, { days: (i+1) * datesPerSlide}));
  }
  return direction === LEFT ? extraDates.reverse() : extraDates;
}

// Build a date slide
// This is also being use by MiniDayPicker
export const buildDateSlide = (
  index: number,
  date: Date,
  selectedDate: Date,
  today: Date,
  handleShift: Function,
  isLoading: boolean,
  dark: boolean,
  todayLabel: string,
  locale: string,
  datesPerSlide: number,
  dateGroupReturnDivider: string,
  isMobile: boolean,
  minDate?: Date,
  maxDate?: Date,
  classNameStr?: string,
  firstVisibleSlideIndex?: number,
  lastVisibleSlideIndex?: number,
  showSingleSlideExtendedDate?: boolean
) => {
  const dateFormatted: string = format(date, standFormat);
  const todayFormatted: string = format(today, standFormat);
  const selectedFormatted: string = format(selectedDate, standFormat);

  let isTabbable: boolean = true;
  let showingMultiple: boolean = false;
  const isToday: boolean = todayFormatted === dateFormatted;
  const isSelected: boolean = selectedFormatted === dateFormatted;
  
  if (firstVisibleSlideIndex && lastVisibleSlideIndex) {
    isTabbable = (index >= firstVisibleSlideIndex && index <= lastVisibleSlideIndex);
    showingMultiple = firstVisibleSlideIndex !== lastVisibleSlideIndex;
  }

  const dateGroupMode: boolean = datesPerSlide > 1;
  // If isLoading only retrieve one or two lines of skeleton
  if (isLoading) {
    return (
      <DateSlide
        key={`skel-${index}`}
        isLoadingGroup={ dateGroupMode }
        isLoading
      >
        {
          !dateGroupMode && <Skeleton
            width='35px'
            height='40%'
          />
        }
        <Skeleton
          width={ dateGroupMode ? '105px' : '65px' }
          height='45%'
        />
      </DateSlide>
    );
  }

  // Handlers for the date button
  const handlers: Handler = {
    onClick: () => {
      handleShift()
    }
  }

  // Determine if we shoulds use Spanish translations
  const isSpanish: boolean = locale === 'es';
  const localeFormat = {locale: isSpanish ? es : undefined}
  // Determine the end date of the date group if using group mode
  const endDate = add(date, {days: datesPerSlide - 1});

  // Establish if the slide is out of range and, therefore, disabled
  let disabled: boolean = false;
  if (minDate) {
    const dateToCompare = dateGroupMode ? endDate : date;
    
    if (isBefore(dateToCompare, minDate)) {
      disabled = true;
    }
  }
  if (maxDate && isAfter(date, maxDate)) {
    disabled = true;
  }

  // Construct the return values for each of the dates. Date group will add both together
  const dateReturnValue: string = '';
  const endDateReturnValue: string = '';

  // Set the value and main text of the slide
  let value: string = '';
  let slideText: string = '';
  if (dateGroupMode) {
    // If we're using date group mode, set up the text to show a
    // range of dates and both months involved if it spans more into
    // another.
    value = `${dateReturnValue}${dateGroupReturnDivider}${endDateReturnValue}`;
    const beginMonthShort = format(date, shortMonthFormat, localeFormat);
    const endMonthShort = format(endDate, shortMonthFormat, localeFormat);
    if (beginMonthShort === endMonthShort) {
      // If the range is contained in a single month, return
      // English: May 28-30
      // Spanish: 38-30 de mayo
      if (isSpanish) {
        slideText = `${format(date, 'd')} - ${format(endDate, 'd')} ${SPANISH_DE} ${format(date, longMonthFormat, localeFormat)}, ${format(date, yearFormat)}`;
      } else {
        slideText = `${format(date, longMonthFormat)} ${format(date, 'd')} - ${format(endDate, 'd')}, ${format(date, yearFormat)}`;
      }
    } else {
      // Provide year at end if different
      // If the range spans two months, return
      // English: May 31 - June 2
      // Spanish: 31 de mayo - 2 de junio
      // with abbreviated months on mobile
      const monthFormat: string = isMobile ? shortMonthFormat : longMonthFormat;
      const startYear: string = format(date, yearFormat);
      const endYear: string = format(endDate, yearFormat);
      const differentYears: boolean = startYear !== endYear;
      const monthOneYear: string = differentYears ? `, ${startYear}` : '';
      if (isSpanish) {
        const firstHalf: string = `${format(date, 'd')} ${SPANISH_DE} ${format(date, monthFormat, localeFormat)}${monthOneYear} -`;
        const secondHalf: string = ` ${format(endDate, 'd')} ${SPANISH_DE} ${format(endDate, monthFormat, localeFormat)}, ${endYear}`;
        slideText = `${firstHalf}${secondHalf}`;
      } else {
        const firstHalf: string = `${format(date, monthFormat)} ${format(date, 'd')}${monthOneYear} -`;
        const secondHalf: string = ` ${format(endDate, monthFormat)} ${format(endDate, 'd')}, ${endYear}`;
        slideText = `${firstHalf}${secondHalf}`;
      }
    }
  } else {
    // If we're not using grouped dates, display
    // English: APR 2
    // Spanish: 2 ABR
    // This will display alongside the day of week / Today label
    // Unless we want to view an extended format for single slides
    value = dateReturnValue;
    const useFormat = (!showingMultiple && showSingleSlideExtendedDate) ? (isSpanish ? 'iii d MMM' : 'iii MMM d') : (isSpanish ? 'd MMM' : 'MMM d');
    slideText = format(date, useFormat, localeFormat);
  }

  // Don't color selected dates out of range as disabled
  const Wrapper: AnyStyledComponent = (!isSelected && disabled) ? DisabledDateSlide : DateSlide;
  const dataAttr: object|undefined = (!isTabbable) ? {'tabIndex': '-1'} : undefined;

  return(
    <Wrapper
      key={dateFormatted}
      isSelected={ isSelected }
      dark={ dark }
      disabled={ disabled }
      dateGroupMode={ dateGroupMode }
      className={isSelected ? 'selected' : ''}
    >
      <Button
        className={classNameStr ? `${classNameStr}-day-button` : ''}
        value={ value }
        ariaLabel={ dateGroupMode ? slideText : format(date, 'MMMM dd yyyy').toUpperCase() }
        handlers={ handlers }
        styleName={ ButtonStyles.DEFAULT }
        disabled={ disabled }
        shape={ ButtonShapes.HOLLOW_BORDERLESS }
        dataAttr={dataAttr}
      >
        <InnerSlideWrapper>
          {!(dateGroupMode || (!showingMultiple && showSingleSlideExtendedDate)) && 
            <InnerDateWrapper>
              { (isToday && todayLabel) ? todayLabel : format(date, 'iii', localeFormat) }
            </InnerDateWrapper>
          }
          <InnerSlideTextWrapper>
            {slideText}
          </InnerSlideTextWrapper>
        </InnerSlideWrapper>
      </Button>
    </Wrapper>
  );
}


// Establish if the given date (group) is on or beyond the max date
const isBeyondMaxDate = (maxDate: Date | undefined, date: Date, datesPerSlide: number) => {
  if (!maxDate) {
    return false;
  }
  const compareDate = add(date, { days: datesPerSlide - 1});
  return isAfter(compareDate, maxDate) || isSameDay(compareDate, maxDate);
}

// Establish if the given date (group) is on or before the min date
const isBeyondMinDate = (minDate: Date | undefined, date: Date) => {
  if (!minDate) {
    return false;
  }
  return isBefore(date, minDate) || isSameDay(date, minDate);
}

const buildArrow = (direction: string, buttonsDisabled: boolean, handleShift: Function, classNameStr?: string) => {
  
  return <Button
    iconName={`carousel-arrow-${direction}`}
    disabled={ buttonsDisabled }
    handlers={ { onClick: () => handleShift(direction === LEFT ? RIGHT : LEFT) } }
    size={ ButtonSizes.SMALL }
    styleName={ ButtonStyles.DEFAULT }
    shape={ ButtonShapes.HOLLOW_BORDERLESS }
    className={classNameStr ? `${classNameStr}-${direction}-arrow`: ''}
  />
}


const DayPicker: FC<DayPickerProps> = ({
  handlers = {},
  desktopDatesPerSlide = 1,
  mobileDatesPerSlide = 1,
  dateGroupReturnDivider = '_to_',
  selectedDate = format(new Date(), propFormat),
  returnedDateFormat,
  mobileBreakpoint = 'sm',
  isMobileWidth,
  todayLabel = 'TODAY',
  locale = 'en',
  desktopSlidesPerView = 7,
  mobileSlidesPerView = 3,
  animationSpeed = .3,
  minDate,
  maxDate,
  isLoading = false,
  showArrows = true,
  theme = {},
  showSingleSlideExtendedDate = false,
  className
}: DayPickerProps) => {
  const {
    dark = false,
    breakpoints = BREAKPOINTS
  } = theme;
  // Break out your calendar, we're keeping track of those special dates
  const modMinDate: Date | undefined = minDate ? makeSafeDate(minDate) : undefined;
  const modMaxDate: Date | undefined = maxDate ? makeSafeDate(maxDate) : undefined;
  const today: Date = new Date();
  const [currentSelectedDate, setSelectedDate] = useState<Date>(makeSafeDate(selectedDate)); // Establish the initial selected date
  const [upcomingSelectedDate, setUpcomingSelectedDate] = useState<Date | null>(null); // The date to color if in transition

  // Figure out if this is mobile width
  const isMobile = typeof isMobileWidth === 'undefined' ? useMedia({ maxWidth: breakpoints[mobileBreakpoint]}) : isMobileWidth;

  // Determine how many slides to show at a time
  const slidesPerView = isMobile ? mobileSlidesPerView : desktopSlidesPerView;

  // Determine how many dates to present on each slide
  const datesPerSlide = isMobile ? mobileDatesPerSlide : desktopDatesPerSlide;

  // Calculate array sizes and offsets
  // Need enough slides hidden on each side of visible set so that
  // clicking on furthest visible date can provide enough slides to view animation
  // without being jarring
  let arraySize: number = (slidesPerView * 2) - 1;
  let showOneSlidePerSwipe:boolean = false;
  if (arraySize === 1) {
    arraySize = 3;
    showOneSlidePerSwipe = true
  }
  // Set each element size to fill the width together
  const elementSize: number = 100 / slidesPerView;
  const baseIndex: number = ((slidesPerView - 1) / 2);
  const offset: number =  (baseIndex || 1) * elementSize;
  const slideStartX: string = `-${offset}%`;
  const centerIndex = (arraySize - 1) / 2;
  const controls = useAnimation();
  const firstVisibleSlideIndex: number = !showOneSlidePerSwipe ? baseIndex : 1;
  const lastVisibleSlideIndex: number = !showOneSlidePerSwipe ? firstVisibleSlideIndex + (slidesPerView - 1) : 1;
 
  // Establish the date array
  let dateArr = buildDateArr(arraySize, currentSelectedDate, centerIndex, datesPerSlide);

  // Update the date array when needed
  // This will combine both a spliced version of the current array with any new given values
  const updateDateArr = (type: string, num: number, value: Date[]) => {
    // Set x location back to initial
    controls.start({
      x: slideStartX,
      transition: { duration: 0 }
    });
    switch (type) {
      case RIGHT:
        const rightCutArr = dateArr.slice(0, dateArr.length - num);
        dateArr = [...value, ...rightCutArr];
      case LEFT:
        const leftCutArr = dateArr.slice(num);
        dateArr = [...leftCutArr, ...value];
      case RESET:
        dateArr = value;
    }
  }

  // Establish if the buttons are temparary disabled or not
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  // handlers for the swiping hook
  const swipeHandlers = useSwipeable({
    onSwiped: (eventData) => {
      handleShift(eventData && eventData.dir && eventData.dir.toLowerCase())
    }
  });

  // Handle the shifting of slides. This is called on:
  // - arrow button press (default 1 movement)
  // - swiping motion detected (default 1 movement)
  // - date button clicked (dynamic)
  // Figures out new x offset for animation and handles restructuring of date arr
  const handleShift: Function = (direction: string, num: number = 1) => {
    // if we're in a disabled state (moved recently), ignore request
    if (buttonsDisabled || isLoading) {
      return;
    }
    // Set x to the required offset.
    // If today's selected date is invalid (against provided min and max dates)
    // don't allow movement
    let x: string = '0';
    let xOffset: number = num * elementSize;
    let moved: boolean = false;
    const movedLeft: boolean = direction === LEFT;
    const movedRight: boolean = direction === RIGHT;
    if (movedLeft && !(isBeyondMaxDate(modMaxDate, currentSelectedDate, datesPerSlide))) {
      moved = true;
      x = `-${offset + xOffset}%`;
    } else if (movedRight && !(isBeyondMinDate(modMinDate, currentSelectedDate))) {
      moved = true;
      x = `-${offset - xOffset}%`;
    }

    // if we did move, trigger the animation and set a timeout for the date array
    // to be updated with new values
    if (moved) {
      setButtonsDisabled(true);
      
      controls.start({
        x: x,
        transition: { duration: animationSpeed }, 
      })
      let newDate: Date;
      if (movedLeft) {
        newDate = add(currentSelectedDate, { days: num * datesPerSlide });
      } else {
        newDate = sub(currentSelectedDate, { days: num * datesPerSlide });
      }

      setUpcomingSelectedDate(newDate); // Set the date we're transitioning to for coloring purposes
      
      setTimeout(() => {
        
        if (movedLeft) {
          updateDateArr(
            LEFT,
            num, 
            buildExtraDates(
              LEFT,
              num,
              dateArr[dateArr.length - 1],
              datesPerSlide
            )
          )
        } else {
          updateDateArr(
            RIGHT,
            num,
            buildExtraDates(
              RIGHT,
              num,
              dateArr[0],
              datesPerSlide
            )
          )
        }
        setSelectedDate(newDate);
        callChangeHandler(newDate);
        setUpcomingSelectedDate(null);
      }, animationSpeed * 1000);
      // After a little bit, return the buttons (and other methods) to functioning status
      setTimeout(() => {
        setButtonsDisabled(false);
      }, (animationSpeed + .1) * 1000)
    }
  }

  // Return the new date using the provided user handlers
  const callChangeHandler = (newDate: Date) => {
    if (handlers.onChange) {
      const firstDateFormatted: string = returnedDateFormat ? format(newDate, returnedDateFormat) : newDate.toString();
      if (datesPerSlide > 1) {
        const secondDate: Date = add(newDate, { days: datesPerSlide - 1});
        const secondDateFormatted: string = returnedDateFormat ? format(secondDate, returnedDateFormat) : secondDate.toString();
        handlers.onChange(`${firstDateFormatted}${dateGroupReturnDivider}${secondDateFormatted}`);
      } else  {
        handlers.onChange(firstDateFormatted);
      }
    }
  }
  
  // on mobile change, reset the array for desired appearance
  useEffect(() => {
    updateDateArr(
      RESET,
      0,
      buildDateArr(arraySize, currentSelectedDate, centerIndex, datesPerSlide)
    )
  }, [isMobile]);

  useEffect(() => {
    const newDate: Date = makeSafeDate(selectedDate);
    setSelectedDate(newDate);
    updateDateArr(
      RESET,
      0,
      buildDateArr(arraySize, newDate, centerIndex, datesPerSlide)
    )
  }, [selectedDate])
  return (
    <DayPickerWrapper
      dark={ dark }
      className={className} 
    >
      { (showArrows && !isLoading) && buildArrow(LEFT, buttonsDisabled, handleShift, className) }
      <SlideWrapper
        showArrows={ showArrows }
        {...swipeHandlers}
      >
            <motion.div
              animate={ controls }
            >
              <Slide
                arraySize={ arraySize }
                elementSize={ elementSize }
              >
                {
                  dateArr.map((date: Date, i: number) => {
                    const direction = i < centerIndex ? RIGHT : LEFT;
                    return <div
                      key={date.toLocaleDateString()}
                      id={date.toLocaleDateString()}
                    >
                      {
                        buildDateSlide(
                          i,
                          date,
                          // Color the date we're transitioning to if it exists
                          upcomingSelectedDate ? new Date(upcomingSelectedDate) : new Date(currentSelectedDate),
                          today,
                          () => handleShift(direction, Math.abs(centerIndex - i)),
                          isLoading,
                          dark,
                          todayLabel,
                          locale,
                          datesPerSlide,
                          dateGroupReturnDivider,
                          isMobile,
                          modMinDate,
                          modMaxDate,
                          className,
                          firstVisibleSlideIndex,
                          lastVisibleSlideIndex,
                          showSingleSlideExtendedDate
                        )
                      }
                    </div>
                  })
                }
              </Slide>
            </motion.div>
      </SlideWrapper>
      { (showArrows && !isLoading) && buildArrow(RIGHT, buttonsDisabled, handleShift, className) }
    </DayPickerWrapper>
  )
};

export default withTheme(DayPicker);